import { IconButton, Slider, Tooltip, Typography } from "@mui/material"
import { CommonService } from "../../Service/CommonService"
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GridColDef, DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import SearchIcon from "@mui/icons-material/Search";
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import "../Dynamic/AddTastingDetail.scss";
import InfoIcon from "../../assets/images/icons/Info.svg";
import Spinner from "../Common/Spinner";
import { InventoryApiService } from "../../Service/InventoryApiService";
import useFormValidation from "./FormValidation";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Select from "react-select";
import { components } from "react-select";

interface LocationState {
  customerServiceRequestId: string;
}
const AddTastingDetail = () => {
    const {id} = useParams<{id: string}>();
    const location = useLocation();
    const history = useHistory();
    const locationState = history.location.state as LocationState;
    const [state, setState] = useState<LocationState | undefined>(locationState);
    const [barrelData, setBarrelData] = useState<any>();
    const [tastingData, setTastingData] = useState<any>();
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownWrapper = useRef<HTMLDivElement>(null);

    const colorOptions = [
        { label: "Gin Clear", value: "#FFFFFF" },
        { label: "White Wine", value: "#FFFBDF" },
        { label: "Pale Staw", value: "#FEEE98" },
        { label: "Pale Gold", value: "#FBEA78" },
        { label: "Jonquiripe Corn", value: "#FBE166" },
        { label: "Yellow Gold", value: "#FADB56" },
        { label: "Amber", value: "#F5C94C" },
        { label: "Deep Gold", value: "#F5C94C" },
        { label: "Amontillado Sherry", value: "#F7C13C" },
        { label: "Burnished", value: "#EAAC08" },
        { label: "Chestnutploroso Sherry", value: "#E79D15" },
        { label: "Russet Muscat", value: "#E79402" },
        { label: "Tawny", value: "#DC7317" },
        { label: "Polished Mahogany", value: "#E06529" },
        { label: "Mahogany", value: "#CC4F30" },
        { label: "Burnt Amber", value: "#B02F29" },
        { label: "Old Oak", value: "#9F241E" },
        { label: "Brown Sherry", value: "#722C1D" },
        { label: "Treacle", value: "#421A0A" }
    ];
  
    const sortAZ = (arr: string[] = []) => [...new Set(arr)].sort((a, b) => a.localeCompare(b));

    const categories = ["CEREAL", "VEGETABLE", "WOODY", "SPICY", "ROASTED", "WINEY", "OFF-FLAVOURS", "TASTE", "FLORAL", "FRUITY", "FLORAL"].filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a.localeCompare(b));

    const subCategoriesMap: Record<string, string[]> = {
        "CEREAL": ["Husk", "Grain"],
        "VEGETABLE": ["Green", "Vegetable", "Earthy"],
        "WOODY": ["Wood"],
        "SPICY": ["Spices"],
        "ROASTED": ["Roasted Flavours", "Malty", "Nuts"],
        "WINEY": ["Sweet Wine", "Spirits"],
        "OFF-FLAVOURS": ["Microbiological", "Technological", "Chemical"],
        "TASTE": ["Basic Taste", "Trigeminal"],
        "FLORAL": ["Blossom"],
        "FRUITY": ["Stone Fruit", "Pipfruit", "Citrus", "Exotic", "Dried Fruit"]
    };

    const attributesMap: Record<string, string[]> = {
        "Grain": ["Wheat", "Barley", "Oat", "Rye", "Corn"],
        "Husk": ["Wheat Bran", "Oat Bran"],
        "Green": ["Hay", "Grass", "Herbal"],
        "Vegetable": ["Pea", "Potato"],
        "Earthy": ["Forest Soil", "Peat", "Mushroom", "Yeast"],
        "Wood": ["Cigar Box", "Resinous", "Oak Wood", "Chestnut Wood"],
        "Spices": ["Pepper", "Nutmeg", "Aniseed", "Coriander", "Ginger", "Vanilla", "Licorice"],
        "Roasted Flavours": ["Smoky", "Fatty", "Coffee"],
        "Malty": ["Caramel", "Chocolate"],
        "Nuts": ["Walnut", "Almond", "Hazelnut"],
        "Sweet Wine": ["Sherry", "Port"],
        "Spirits": ["Brandy", "Rum"],
        "Microbiological": ["Oily", "Waxy", "Acetic Acid", "Acetaldehyde"],
        "Technological": ["Head/Solvent", "Tail/Fusel", "Burnt"],
        "Chemical": ["Sulfurous", "Rubbery", "Oxidized", "Medical", "Soady"],
        "Basic Taste": ["Sweet", "Sour", "Bitter"],
        "Trigeminal": ["Astringent", "Burning", "Stinging"],
        "Blossom": ["Lavender", "Carnation", "Geranium", "Honey"],
        "Stone Fruit": ["Apricot", "Plum", "Peach"],
        "Pipfruit": ["Apple", "Pear", "Quince"],
        "Citrus": ["Lemon", "Orange", "Grapefruit"],
        "Exotic": ["Banana", "Kiwi", "Hop"],
        "Dried Fruit": ["Raisin", "Fig"]
    };

    const {
      formData,
      errors,
      handleBlur,
      setErrors,
      validateForm,
      setFormData,
    } = useFormValidation(tastingData);
    
    const routeBack = () => {
      history.goBack();
    };

    useEffect(() => {
        fetchBarrelById();
        getTastingDetails();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                dropdownWrapper.current &&
                !dropdownWrapper.current.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getTastingDetails = async () => {
        try {
            const response = await InventoryApiService.getTastingDetails(id, state?.customerServiceRequestId);
            setTastingData(response.data);
            setFormData(response.data);
        } catch (error) {
            console.error("Error fetching companies:", error);
        }
    };
    
    const fetchBarrelById = async () => {
        try {
            const response = await InventoryApiService.getBarrelById(id);
            setBarrelData(response.data);
        } catch (error) {
            console.error("Error fetching companies:", error);
        }
    };

    const handleSliderChange = (name: string) => (event: Event, value: number) => {
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        const parsedValue =
            value === "true" ? true : value === "false" ? false : value;

        setFormData((prev) => ({
            ...prev,
            [name]: parsedValue
        }));

        // Clear validation error when user edits the field
        if (errors?.[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const onSelectCategory = (cat: string) => {
        setFormData(prev => ({
            ...prev,
            category: cat,
            subCategory: null,
            attribute: null
        }));
    };

    const onSelectSubCategory = (subCat: string) => {
         setFormData(prev => ({
            ...prev,
            subCategory: subCat,
            attribute: null
        }));
    };

    const onSelectAttribute = (attr: string) => {
        setFormData(prev => ({
            ...prev,
            attribute: attr
        }));
    };

    const validateRequireFields = () => {
        let isValidationPass = true;
        let newErrors: any = {};

        const requiredFields = [
            { field: "blenderComments", error: "Please enter Comments" }
        ];

        requiredFields.forEach(({ field, error }) => {
            if (!formData?.[field] || formData[field].trim().length === 0) {
                newErrors[field] = error;
                isValidationPass = false;
            }
        });

        setErrors(newErrors);
        return isValidationPass;
    };
    
    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const isFormValid = await validateRequireFields();

        if (!isFormValid) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const payload = {
            ...formData,
            barrelId: id,
            barrelNumber: barrelData?.barrelNumber,
            customerServiceRequestId: state?.customerServiceRequestId,
            containerType: barrelData?.containerType
        };

        try {
            let response = await InventoryApiService.addTastingNote(payload);
            CommonService.Toast.fire({
                title: "Tasting details saved successfully",
                icon: "success",
            });
            history.goBack();
        } catch (error: any) {
            CommonService.Toast.fire({
                title: "Failed to save tasting details",
                icon: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        setFormData(prev => {
            if (prev.attribute) {
                return { ...prev, attribute: null };
            }

            if (prev.subCategory) {
                return { ...prev, subCategory: null, attribute: null };
            }

            if (prev.category) {
                return { ...prev, category: null, subCategory: null, attribute: null };
            }

            return prev;
        });
    };
    
    return (
        <>
            <div
                className="usersList usersList__user add-tasting-history-detail container-history-detail"
                style={{ paddingBottom: CommonService.isAdmin() ? 47 : "" }}>
                <div className="usersList__header--wrapper d-flex flex-wrap align-items-center w-100">
                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2 position-relative">
                        <div className="d-flex align-items-center gap-2 flex-wrap left-wrapper">
                            <div className="back__button" onClick={routeBack}>

                                <IconButton
                                    aria-label="ArrowBackIcon"
                                    sx={{ backgroundColor: "#FFF" }}
                                >
                                    <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                                </IconButton>
                            </div>

                            <div className="header--title">
                                <h1 className="title mb-0">
                                    {barrelData?.productName} ({barrelData?.lotNo})
                                </h1>
                            </div>
                            <div className="divider mx-2"></div>
                            <div className="header--title d-flex flex-wrap me-auto mb-xxl-0 gap-3">
                                <div className="time__details">
                                    <Typography
                                        variant="h6"
                                        sx={{ color: "#333333", fontSize: "12px", fontWeight: 400 }}
                                    >
                                        Container Number
                                    </Typography>
                                    <Typography
                                        variant="h3"
                                        sx={{ color: "#333333", fontSize: "18px", fontWeight: 700 }}
                                    >
                                        {barrelData?.barrelNumber}
                                    </Typography>
                                </div>
                                <div className="time__details">
                                    <Typography
                                        variant="h6"
                                        sx={{ color: "#333333", fontSize: "12px", fontWeight: 400 }}
                                    >
                                        Container Type
                                    </Typography>
                                    <Typography
                                        variant="h3"
                                        sx={{ color: "#333333", fontSize: "18px", fontWeight: 700 }}
                                    >
                                        {barrelData?.containerType}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="user-table-container position-relative">
                    <div className="userTable">
                        <div className="service-req--inner container">
                            <div className="tasting-history-detail-wrapper edit-history-detail-wrapper service-req-form-wrapper">
                                <form className="service-req-form"
                                onSubmit={onSubmit} 
                                >
                                    <div className="row gy-3">
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Nose Notes
                                                    <Tooltip placement="bottom-start" title="Describe the aroma: dominant and secondary notes. Use short descriptors separated by commas (e.g., “vanilla, toasted oak, citrus, herbal grain”)." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <textarea 
                                                    className={`form-control service-req-input ${errors.noseNotes ? "is_invalid" : ""}`}
                                                    placeholder="Describe aroma: vanilla, spice, grain…" 
                                                    id="noseNotes"
                                                    name="noseNotes"
                                                    value={formData?.noseNotes}
                                                    onChange={handleChange}
                                                    rows={3} 
                                                    maxLength={5000} 
                                                    style={{ resize: "none" }}
                                                />
                                                <p className="max-char-info text-length">Maximum length 5000 characters. {formData?.noseNotes?.length ?? 0}/5000</p>
                                                {errors.noseNotes && (
                                                    <div className="error-message">{errors.noseNotes}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Palate Notes
                                                    <Tooltip placement="bottom-start" title="Describe taste and mouthfeel: sweetness, texture, spice, and how the flavor develops on the palate (e.g., “caramel entry, rising spice, oily mouthfeel”)." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <textarea 
                                                    className={`form-control service-req-input ${errors.palateNotes ? "is_invalid" : ""}`}
                                                    placeholder="Describe taste and mouthfeel…"
                                                    id="palateNotes"
                                                    name="palateNotes"
                                                    value={formData?.palateNotes}
                                                    onChange={handleChange}
                                                    rows={3} 
                                                    maxLength={5000} 
                                                    style={{ resize: "none" }}
                                                />
                                                <p className="max-char-info text-length">Maximum length 5000 characters. {formData?.palateNotes?.length ?? 0}/5000</p>
                                                {errors.palateNotes && (
                                                    <div className="error-message">{errors.palateNotes}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Finish Notes
                                                    <Tooltip placement="bottom-start" title="Describe the aftertaste and length: short/medium/long, warmth, and lingering notes (e.g., “long warm finish with clove and oak tannin”)." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <textarea 
                                                    className={`form-control service-req-input ${errors.finishNotes ? "is_invalid" : ""}`}
                                                    placeholder="Describe finish and length…" 
                                                    id="finishNotes"
                                                    name="finishNotes"
                                                    value={formData?.finishNotes}
                                                    onChange={handleChange}
                                                    rows={3} 
                                                    maxLength={5000} 
                                                    style={{ resize: "none" }}
                                                />
                                                <p className="max-char-info text-length">Maximum length 5000 characters. {formData?.finishNotes?.length ?? 0}/5000</p>
                                                {errors.finishNotes && (
                                                    <div className="error-message">{errors.finishNotes}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Sweetness
                                                    <Tooltip placement="bottom-start" title="How sweet does the spirit taste? Consider honey, caramel, sugar, or natural grain sweetness." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <Slider
                                                    id="sweetness"
                                                    name="sweetness"
                                                    value={Number(formData?.sweetness) || 0}
                                                    onChange={handleSliderChange("sweetness")}
                                                    onBlur={handleBlur}
                                                    max={10} 
                                                    valueLabelDisplay="auto" 
                                                    sx={{ height: "8px", color: "#0C3344" }} 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Spice
                                                    <Tooltip placement="bottom-start" title="Perceived spice heat or baking-spice character (pepper, clove, cinnamon). Higher numbers indicate stronger spice presence." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <Slider
                                                    id="spice"
                                                    name="spice"
                                                    value={Number(formData?.spice) || 0}
                                                    onChange={handleSliderChange("spice")}
                                                    onBlur={handleBlur}
                                                    max={10} 
                                                    valueLabelDisplay="auto" 
                                                    sx={{ height: "8px", color: "#0C3344" }} 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Earthiness
                                                    <Tooltip placement="bottom-start" title="Herbal, earthy, tobacco, leather, or minty elements. Rate intensity of these non-sweet, non-fruit tones." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <Slider
                                                    id="earthiness"
                                                    name="earthiness"
                                                    value={Number(formData?.earthiness) || 0}
                                                    onChange={handleSliderChange("earthiness")}
                                                    onBlur={handleBlur}
                                                    max={10} 
                                                    valueLabelDisplay="auto" 
                                                    sx={{ height: "8px", color: "#0C3344" }} 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Fruitiness
                                                    <Tooltip placement="bottom-start" title="Relative intensity of fruit notes (fresh, dried, or tropical). Use the scale to indicate strength." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <Slider
                                                    id="fruitiness"
                                                    name="fruitiness"
                                                    value={Number(formData?.fruitiness) || 0}
                                                    onChange={handleSliderChange("fruitiness")}
                                                    onBlur={handleBlur}
                                                    max={10} 
                                                    valueLabelDisplay="auto" 
                                                    sx={{ height: "8px", color: "#0C3344" }} 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Oak Presence
                                                    <Tooltip placement="bottom-start" title="Strength of oak-derived notes: vanilla, toast, tannin, char, or wood influence. Rate overall oak impact." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <Slider
                                                    id="oakPresence"
                                                    name="oakPresence"
                                                    value={Number(formData?.oakPresence) || 0}
                                                    onChange={handleSliderChange("oakPresence")}
                                                    onBlur={handleBlur}
                                                    max={10} 
                                                    valueLabelDisplay="auto" 
                                                    sx={{ height: "8px", color: "#0C3344" }} 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Balance
                                                    <Tooltip placement="bottom-start" title="How well the aroma, palate, and finish work together.
                                                            1 = unbalanced/sharp, 5 = harmonious/integrated." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <Slider
                                                    id="balance"
                                                    name="balance"
                                                    value={Number(formData?.balance) || 0}
                                                    onChange={handleSliderChange("balance")}
                                                    onBlur={handleBlur}
                                                    max={10} 
                                                    valueLabelDisplay="auto" 
                                                    sx={{ height: "8px", color: "#0C3344" }} 
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Overall Score
                                                    <Tooltip placement="bottom-start" title="Overall panel score for this sample. Use the full range sparingly; typical commercial-quality containers fall between 5–9." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <Slider
                                                    id="overallScore"
                                                    name="overallScore"
                                                     value={Number(formData?.overallScore) || 0}
                                                    onChange={handleSliderChange("overallScore")}
                                                    onBlur={handleBlur}
                                                    defaultValue={formData?.overallScore} 
                                                    max={10} 
                                                    valueLabelDisplay="auto" 
                                                    sx={{ height: "8px", color: "#0C3344" }} 
                                                />
                                         </div>
                                        </div>

                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Blend Group
                                                    <Tooltip placement="bottom-start" title="Select the primary blending role this sample should play. Choose the single category that best represents its dominant character (e.g., Sweet Aromatic, Spicy & Bold)." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <div className="blend-grp-wrapper" ref={dropdownWrapper}>
                                                    <input
                                                        type="text"
                                                        tabIndex={-1}
                                                        readOnly
                                                        value={
                                                            formData?.category && formData?.subCategory && formData?.attribute
                                                                ? `${formData.category} - ${formData.subCategory} - ${formData.attribute}`
                                                                : formData?.category && formData?.subCategory ? `${formData.category} - ${formData.subCategory}` : formData?.category ? `${formData.category}` : ""
                                                        }
                                                        placeholder="Select Blend Group"
                                                        className="form-control form-select service-req-input"
                                                        onFocus={() => setShowDropdown(true)} // Show on input click
                                                    />

                                                    {showDropdown && (
                                                        <div className="blend-grp-menu">
                                                            <div className="grp-menu-header">
                                                                {(formData?.category || formData?.subCategory || formData?.attribute) && (
                                                                <button
                                                                type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleBack();
                                                                    }}
                                                                >
                                                                    <svg width="5" height="10" viewBox="0 0 5 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M4.87791 9.22365C4.91662 9.26588 4.94732 9.31601 4.96827 9.37119C4.98922 9.42636 5 9.4855 5 9.54522C5 9.60495 4.98922 9.66408 4.96827 9.71926C4.94732 9.77444 4.91662 9.82457 4.87791 9.8668C4.8392 9.90903 4.79324 9.94253 4.74267 9.96538C4.6921 9.98824 4.63789 10 4.58315 10C4.52841 10 4.4742 9.98824 4.42363 9.96538C4.37305 9.94253 4.3271 9.90903 4.28839 9.8668L0.122189 5.32157C0.0834531 5.27936 0.0527239 5.22923 0.0317578 5.17405C0.0107917 5.11888 0 5.05973 0 5C0 4.94027 0.0107917 4.88112 0.0317578 4.82594C0.0527239 4.77077 0.0834531 4.72064 0.122189 4.67842L4.28839 0.133201C4.36656 0.0479136 4.47259 -2.37759e-09 4.58315 0C4.6937 2.37759e-09 4.79973 0.0479136 4.87791 0.133201C4.95608 0.218487 5 0.334161 5 0.454775C5 0.575389 4.95608 0.691063 4.87791 0.77635L1.00594 5L4.87791 9.22365Z" fill="white" />
                                                                    </svg>
                                                                </button>
                                                                )}
                                                                <p className="header-breadcrumb">
                                                                    <span>{formData?.category || "Select Category"}</span><span> / {formData?.subCategory || "Select Sub Category"}</span><span> / {formData?.attribute || "Select Attribute"}</span>
                                                                </p>
                                                            </div>
                                                            <div className="grp-menu-body">
                                                                {/* Category Menu */}
                                                                {!formData?.category && <ul className="grp-list-wrapper cat-list">
                                                                    {categories.map((cat) => (
                                                                        <li key={cat} className={cat === formData?.category ? "selected" : ""} onClick={() => onSelectCategory(cat)}>
                                                                            {cat}
                                                                        </li>
                                                                    ))}
                                                                </ul>}
                                                                {/* Sub Category Menu */}
                                                                {formData?.category && !formData?.subCategory && (
                                                                    <ul className="grp-list-wrapper sub-cal-list">

                                                                        {sortAZ(subCategoriesMap[formData.category]).map((subCat) => (
                                                                            <li key={subCat} className={subCat === formData?.subCategory ? "selected" : ""} onClick={() => onSelectSubCategory(subCat)}>
                                                                                {subCat}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                                {/* Attribute Menu  */}
                                                                {formData?.subCategory && (
                                                                    <ul className="grp-list-wrapper attr-list">
                                                                        {sortAZ(attributesMap[formData.subCategory]).map((attr) => (
                                                                            <li
                                                                                key={attr}
                                                                                className={attr === formData?.attribute ? "selected" : ""}
                                                                                onClick={() => onSelectAttribute(attr)}>
                                                                                {attr}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        </div>)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Color
                                                    <Tooltip placement="bottom-start" title="Choose the closest color match. Color indicates extraction and maturity; evaluate using the container sample in a clear glass under neutral light." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>

                                                <Select
                                                id="user-company-dropdown"
                                                className="w-100 select-dropdown multi-select-dropdown"
                                                options={colorOptions}
                                                placeholder="Select Color"
                                                value={colorOptions.find(
                                                    (opt) => opt.value === formData?.colorCode
                                                )}
                                                onChange={(option) => {
                                                    setFormData((prev) => ({
                                                    ...prev,
                                                    colorName: option?.label || "",
                                                    colorCode: option?.value || ""
                                                    }));
                                                }}
                                                onBlur={handleBlur}
                                                isSearchable
                                                components={{
                                                    DropdownIndicator: () => <ExpandMoreRoundedIcon />,
                                                    IndicatorSeparator: () => null,
                                                    Option: (props) => {
                                                    const { label, value } = props.data;
                                                    return (
                                                        <components.Option {...props}>
                                                        <div className="color-dropdown-wrapper">
                                                            <span
                                                            style={{
                                                                backgroundColor: value,
                                                            }}
                                                            className="color-wrapper"
                                                            ></span>
                                                            {label}
                                                        </div>
                                                        </components.Option>
                                                    );
                                                    }
                                                }}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Suitable for Direct Bottling
                                                    <Tooltip placement="bottom-start" title="Is this container suitable for bottling as-is without blending?" arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <div className="d-flex align-items-center">
                                                    <div className="d-inline-flex">
                                                        <input
                                                            className="custom-radio service-req-input"
                                                            type="radio"
                                                            name="isSuitableForDirectBottling"
                                                            id="suitable-for-direct-bottling-yes"
                                                            value="true"
                                                            checked={formData?.isSuitableForDirectBottling === true}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        <label
                                                            className="custom-radio-label"
                                                            htmlFor="suitable-for-direct-bottling-yes"
                                                        >
                                                            Yes
                                                        </label>
                                                    </div>
                                                    <div className="d-inline-flex ms-2 me-2">
                                                        <input
                                                            className="custom-radio service-req-input"
                                                            type="radio"
                                                            name="isSuitableForDirectBottling"
                                                            id="suitable-for-direct-bottling-no"
                                                            value="false"
                                                            checked={formData?.isSuitableForDirectBottling === false}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        <label
                                                            className="custom-radio-label"
                                                            htmlFor="suitable-for-direct-bottling-no"
                                                        >
                                                            No
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Hold for Aging
                                                    <Tooltip placement="bottom-start" title="Should this container continue aging or resting to improve maturity or balance? Select “Yes” if the panel recommends additional development time." arrow>
                                                        <img src={InfoIcon} alt="Info" height={18} width={18} />
                                                    </Tooltip>
                                                </label>
                                                <div className="d-flex align-items-center">
                                                    <div className="d-inline-flex">
                                                        <input
                                                            className="custom-radio service-req-input"
                                                            type="radio"
                                                            name="isHoldForAging"
                                                            id="hold-for-aging-yes"
                                                            value="true"
                                                            checked={formData?.isHoldForAging === true}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        <label
                                                            className="custom-radio-label"
                                                            htmlFor="hold-for-aging-yes"
                                                        >
                                                            Yes
                                                        </label>
                                                    </div>
                                                    <div className="d-inline-flex ms-2 me-2">
                                                        <input
                                                            className="custom-radio service-req-input"
                                                            type="radio"
                                                            name="isHoldForAging"
                                                            id="hold-for-aging-no"
                                                            value="false"
                                                            checked={formData?.isHoldForAging === false}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        <label
                                                            className="custom-radio-label"
                                                            htmlFor="hold-for-aging-no"
                                                        >
                                                            No
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label">Taster Name</label>
                                                <input
                                                    type="text"
                                                    id="tasterName"
                                                    name="tasterName"
                                                    placeholder="Enter taster name"
                                                    value={formData?.tasterName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    className={`form-control service-req-input ${errors.tasterName ? "is_invalid" : ""}`}
                                                />
                                                {errors.tasterName && (
                                                    <div className="error-message">{errors.tasterName}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-sm-6 col-md-4 col-lg-6 col-xl-4">
                                            <div className="form__field--wrapper">
                                                <label className="block mb-2 form-label"><strong>Comments{" "}</strong> <span className="star-required text-danger">*</span>
                                                </label>
                                                <textarea
                                                    className={`form-control ${errors.blenderComments ? "is_invalid" : ""}`}
                                                    placeholder="Any additional comments or recommendations…"
                                                    id="blenderComments"
                                                    name="blenderComments"
                                                    value={formData?.blenderComments}
                                                    onChange={handleChange}
                                                    rows={3} 
                                                    maxLength={5000} 
                                                    style={{ resize: "none" }}
                                                />
                                                <p className="max-char-info text-length">Maximum length 5000 characters. {formData?.blenderComments?.length ?? 0}/5000</p>
                                                {errors.blenderComments && (
                                                    <div className="error-message">{errors.blenderComments}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                <button
                                                    type="button" 
                                                    onClick={routeBack}
                                                    className="btn secondary-btn register-btn mb-2"
                                                    disabled={isLoading}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn primary-btn register-btn mb-2"
                                                    disabled={isLoading}
                                                >
                                                    {isLoading && <Spinner />}
                                                    Submit
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
export default AddTastingDetail;